import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ComponentProps } from 'react';
import { useRef, useState } from 'react';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { useResultsStoryAssets } from '@sorare/core/src/components/animations/AnimatedStory/useResultsStoryAssets';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { isType } from '@sorare/core/src/lib/gql';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { FixtureOverviewTasks } from 'components/fixture/FixtureTeamsOverview/FixtureOverview/FixtureOverviewTasks';
import { RewardConfigsOverview } from 'components/rewards/RewardConfigsOverview';
import { RewardConfigsOverviewHeader } from 'components/rewards/RewardConfigsOverviewHeader';
import { LastFixtureStoryStep } from 'components/so5/LastFixtureStory/LastFixtureStoryStep';
import type { StoryState } from 'components/so5/LastFixtureStory/types';
import { useClaimRewards } from 'hooks/so5/useClaimRewards';

import { Cta } from '../LastFixtureStoryStep/Cta';
import { Title } from '../LastFixtureStoryStep/Title';
import { VerificationBanner } from '../VerificationBanner';
import { DeliverableItemBlock } from './DeliverableItemBlock';
import type {
  RewardsRecap_anyRewardConfigInterface,
  RewardsRecap_rarityRewardConfigs,
  RewardsRecap_so5Fixture,
  RewardsRecap_so5Reward,
} from './__generated__/index.graphql';
import type { RewardByType } from './useRewardsVerification';
import { useRewardsVerification } from './useRewardsVerification';

const RewardsRecapWrapper = styled(Vertical)`
  @media ${laptopAndAbove} {
    min-width: 420px;
  }
`;
const FullWidthVertical = styled(Vertical).attrs({ gap: 3 })`
  width: 100%;
  max-width: 600px;
`;

const getAutoClaimableRewards = <T extends object>(
  claimableRewardsByType: RewardByType<T>,
  useRewardChoicesV2: boolean
) => {
  if (useRewardChoicesV2) {
    const {
      total,
      card,
      cash,
      shards,
      ...cardCashShardsExcludedClaimableRewards
    } = claimableRewardsByType;
    return cardCashShardsExcludedClaimableRewards;
  }
  const { total, card, ...cardExcludedClaimableRewards } =
    claimableRewardsByType;
  return cardExcludedClaimableRewards;
};

// This function is replacing the 'so5Rewards[number]['rewardConfigs'][number]' object
// by the associated 'so5Rewards[number]['alternativeRewardConfigs'][number]['rewardConfigs'][number]'
// when the user select the alternative reward choice; we have to do this because
// so5Rewards are memoized in a useState to prevent the claim mutation to close the rewards flow
const extractAndReplaceRewardConfigs = (
  so5Rewards: RewardsRecap_so5Reward[],
  so5RewardsWithSignedAlternative?: RewardWithSignedAlternative[],
  multiplied = false
):
  | RewardsRecap_so5Reward['rewardConfigsMultiplied']
  | RewardsRecap_so5Reward['rewardConfigsNonMultiplied']
  | NonNullable<
      RewardsRecap_so5Reward['alternativeRewardConfigs']
    >[number]['rewardConfigs'] => {
  if (!so5RewardsWithSignedAlternative) {
    return so5Rewards.flatMap(
      ({ rewardConfigsMultiplied, rewardConfigsNonMultiplied }) =>
        multiplied ? rewardConfigsMultiplied : rewardConfigsNonMultiplied
    );
  }

  return [...so5Rewards].flatMap(so5Reward => {
    const selectedAlternativeReward = so5Reward.alternativeRewardConfigs?.find(
      ({ signedAlternativeRewardConfigs }) => {
        const associatedso5RewardWithSignedAlternative =
          so5RewardsWithSignedAlternative.find(
            ({ so5RewardId }) => so5RewardId === so5Reward.id
          );

        return (
          !!associatedso5RewardWithSignedAlternative &&
          associatedso5RewardWithSignedAlternative.signedAlternativeRewardConfigs ===
            signedAlternativeRewardConfigs
        );
      }
    );

    if (!selectedAlternativeReward) {
      return multiplied
        ? so5Reward.rewardConfigsMultiplied
        : so5Reward.rewardConfigsNonMultiplied;
    }

    return selectedAlternativeReward.rewardConfigs;
  });
};

type RewardWithSignedAlternative = {
  so5RewardId: string;
  signedAlternativeRewardConfigs: string | null;
};
type Props = {
  so5Fixture?: RewardsRecap_so5Fixture;
  so5Rewards: RewardsRecap_so5Reward[];
  boostedRewardConfigsByRarity?: RewardsRecap_rarityRewardConfigs[];
  so5RewardsWithSignedAlternative?: RewardWithSignedAlternative[];
  onNext?: () => void;
  onClose: ComponentProps<typeof LastFixtureStoryStep>['onClose'];
  from?: StoryState;
};
export const RewardsRecap = ({
  from: fromProp,
  so5Fixture,
  so5Rewards,
  boostedRewardConfigsByRarity,
  so5RewardsWithSignedAlternative,
  onNext,
  onClose,
}: Props) => {
  const {
    flags: { useRewardChoicesV2 = false },
  } = useFeatureFlags();

  const from = useRef(fromProp);
  const { claimableRewardsByType, rewardsByType, verificationNeeded } =
    useRewardsVerification(so5Rewards);

  const [claimed, setClaimed] = useState(false);
  const [claimRewards] = useClaimRewards();

  const { intro, tunnel } = useResultsStoryAssets();

  const { currentUser } = useCurrentUserContext();

  const deliverableItemOrders = claimed
    ? currentUser?.unclaimedDeliverableItemOrders || []
    : [];

  const autoClaimableRewards = getAutoClaimableRewards(
    claimableRewardsByType,
    !!useRewardChoicesV2
  );

  const autoClaimableRewardIds = Object.values(autoClaimableRewards).flatMap(
    reward => reward.map(({ id }) => id)
  );

  if (!claimed && autoClaimableRewardIds.length) {
    claimRewards({ so5RewardIds: autoClaimableRewardIds });
    setClaimed(true);
  }

  const rewardConfigs = extractAndReplaceRewardConfigs(
    so5Rewards,
    so5RewardsWithSignedAlternative
  );

  const multipliedRewardConfigs = extractAndReplaceRewardConfigs(
    so5Rewards,
    so5RewardsWithSignedAlternative,
    true
  );

  return (
    <LastFixtureStoryStep
      title={<Title type="rewardsRecap" so5Fixture={so5Fixture} />}
      video={{
        asset: from.current === 'intro' ? intro : tunnel,
        color: 'var(--c-neutral-600)',
      }}
      onNext={onNext}
      onClose={onClose}
      cta={
        <Cta
          so5FixtureSlug={so5Fixture?.slug}
          eventType={so5Fixture?.type}
          onNext={onNext}
          onClose={onClose}
        />
      }
      banner={<VerificationBanner verificationNeeded={verificationNeeded} />}
    >
      <FullWidthVertical>
        <RewardsRecapWrapper>
          <RewardConfigsOverviewHeader
            so5Fixture={so5Fixture}
            multipliedRewardConfigs={multipliedRewardConfigs}
            verificationNeeded={verificationNeeded}
            onClose={onClose}
            onlyRewards
          />
          <RewardConfigsOverview
            rewardConfigs={rewardConfigs}
            boostedRewardConfigsByRarity={boostedRewardConfigsByRarity || []}
            so5Lineups={[]}
            prepend={
              !!so5Fixture && <FixtureOverviewTasks so5Fixture={so5Fixture} />
            }
          />
        </RewardsRecapWrapper>
        <DeliverableItemBlock
          deliverableItemOrders={deliverableItemOrders}
          deliverableItems={rewardsByType.deliverableItem
            .flatMap(so5Reward => so5Reward.rewards)
            .filter(reward => isType(reward, 'DeliverableItemReward'))}
        />
      </FullWidthVertical>
    </LastFixtureStoryStep>
  );
};

const anyRewardConfigInterfaceFragment = gql`
  fragment RewardsRecap_anyRewardConfigInterface on AnyRewardConfigInterface {
    id
    boostedRewardConfigs {
      id
      ...RewardConfigsOverview_anyRewardConfigInterface
    }
    ...RewardConfigsOverview_anyRewardConfigInterface
  }
  ${RewardConfigsOverview.fragments.anyRewardConfigInterface}
` as TypedDocumentNode<RewardsRecap_anyRewardConfigInterface>;

RewardsRecap.fragments = {
  rarityRewardConfigs: gql`
    fragment RewardsRecap_rarityRewardConfigs on RarityRewardConfigs {
      ...RewardConfigsOverview_rarityRewardConfigs
    }
    ${RewardConfigsOverview.fragments.rarityRewardConfigs}
  ` as TypedDocumentNode<RewardsRecap_rarityRewardConfigs>,
  so5Reward: gql`
    fragment RewardsRecap_so5Reward on So5Reward {
      slug
      id
      rewards {
        id
        ...DeliverableItemBlock_deliverableItemReward
      }
      alternativeRewardConfigs {
        signedAlternativeRewardConfigs
        rewardConfigs {
          id
          ...RewardsRecap_anyRewardConfigInterface
        }
      }
      rewardConfigsNonMultiplied: rewardConfigs(multiplyBaseRewards: false) {
        id
        ...RewardsRecap_anyRewardConfigInterface
      }
      rewardConfigsMultiplied: rewardConfigs(multiplyBaseRewards: true) {
        id
        ...RewardsRecap_anyRewardConfigInterface
      }
      ...useRewardsVerification_so5Reward
    }
    ${useRewardsVerification.fragments.so5Reward}
    ${DeliverableItemBlock.fragments.deliverableItemReward}
    ${anyRewardConfigInterfaceFragment}
  ` as TypedDocumentNode<RewardsRecap_so5Reward>,
  so5Fixture: gql`
    fragment RewardsRecap_so5Fixture on So5Fixture {
      slug
      type
      gameWeek
      mySo5Lineups {
        id
        ...RewardConfigsOverview_so5Lineup
      }
      ...FixtureOverviewTasks_so5Fixture
      ...Title_so5Fixture
      ...RewardConfigsOverviewHeader_so5Fixture
    }
    ${FixtureOverviewTasks.fragments.so5Fixture}
    ${RewardConfigsOverviewHeader.fragments.so5Fixture}
    ${RewardConfigsOverview.fragments.so5Lineup}
    ${Title.fragments.so5Fixture}
  ` as TypedDocumentNode<RewardsRecap_so5Fixture>,
};
